/**
 * Solomon Action Engine — registry.
 *
 * Module-singleton map of actionId → ActionDefinition. Actions
 * register themselves at import time via `registerAction(...)` from
 * their own module; `actions/index.ts` imports each one for side
 * effect so a single import of the barrel ensures every action is
 * available.
 *
 * Why a global registry instead of passing definitions through React
 * context?
 * ----------------------------------------------------------------
 * Action definitions are pure data + functions — they don't depend
 * on any per-render state. A global registry means:
 *   - The Solomon LLM planner can validate `actionId`s without a
 *     React tree (it imports the registry from a worker / node-side
 *     unit test).
 *   - Tree-shaking still works because the barrel is the only
 *     side-effecting import; consumers that don't need actions can
 *     skip the barrel.
 *   - Tests can register stub actions in a `beforeEach` without
 *     wrapping in a provider.
 *
 * The audit trail is still per-execution — the registry only holds
 * definitions, not execution state.
 */

import type {
  ActionDefinition,
  ActionExecutionContext,
  ActionPermissionTier,
  ActionProposal,
  ActionRejection,
  AnyActionDefinition,
  PreparedAction,
} from "./types";

/**
 * The registry. Module-scoped Map; no exported reference so callers
 * can't bypass `registerAction` (which does validation).
 */
const REGISTRY = new Map<string, AnyActionDefinition>();

/**
 * Register an action definition. Idempotent — re-registering with
 * the same id replaces the prior definition (useful for hot reload
 * and test stubs). Logs a warning in dev when replacement happens
 * so accidental id collisions surface.
 */
export function registerAction<P>(definition: ActionDefinition<P>): void {
  if (!definition.id || typeof definition.id !== "string") {
    throw new Error(
      `registerAction: definition.id must be a non-empty string (got ${JSON.stringify(definition.id)})`,
    );
  }
  if (REGISTRY.has(definition.id) && import.meta.env.DEV) {
    console.warn(
      `[solomon-action-engine] Replacing existing action definition for "${definition.id}". ` +
        `This is expected during hot reload; otherwise check for duplicate ids.`,
    );
  }
  REGISTRY.set(definition.id, definition as AnyActionDefinition);
}

/**
 * Look up an action by id. Returns null when the id isn't
 * registered — callers should surface this as a rejection rather
 * than throwing, because the id often comes from an LLM and we
 * don't want a hallucinated action to crash the panel.
 */
export function getAction(id: string): AnyActionDefinition | null {
  return REGISTRY.get(id) ?? null;
}

/**
 * List every registered action. Used by the admin / dev UI to
 * render the action catalog and by the Solomon planner to know what
 * verbs are available.
 */
export function listActions(): AnyActionDefinition[] {
  return Array.from(REGISTRY.values());
}

/**
 * Test-only: wipe the registry. Exported because re-importing the
 * module isn't viable in Vitest's worker model — once the actions
 * barrel has run its side effects, the only way back to a clean
 * slate is an explicit clear.
 */
export function __resetActionRegistryForTests(): void {
  REGISTRY.clear();
}

// ---------------------------------------------------------------------------
// Permission tier ordering
// ---------------------------------------------------------------------------

/**
 * Numeric weight of each tier. Higher tier satisfies lower
 * requirements (a platform_admin can run a member-tier action).
 * Kept as a function rather than a Record so the comparison reads
 * naturally in `userMeetsTier`.
 */
const TIER_RANK: Record<ActionPermissionTier, number> = {
  member: 0,
  staff: 1,
  org_admin: 2,
  platform_admin: 3,
};

export function userMeetsTier(
  userTier: ActionPermissionTier,
  required: ActionPermissionTier,
): boolean {
  return TIER_RANK[userTier] >= TIER_RANK[required];
}

// ---------------------------------------------------------------------------
// Preparation
// ---------------------------------------------------------------------------

export interface PrepareActionOptions {
  /**
   * The caller's permission tier. Resolved by the engine hook from
   * the current user's role; we accept it as a parameter so this
   * function stays pure / testable.
   */
  userTier: ActionPermissionTier;
  /**
   * Force the engine to require confirmation regardless of
   * `definition.defaultRequiresConfirm`. Used by Solomon when the
   * action is proposed with low confidence.
   */
  forceConfirm?: boolean;
}

/**
 * Validate a proposal and build a PreparedAction. Never throws —
 * problems are returned as `rejection` on the prepared object so the
 * UI can render an inline "can't do this because…" message instead
 * of an error boundary.
 */
export async function prepareAction<P = Record<string, unknown>>(
  proposal: ActionProposal<P>,
  ctx: ActionExecutionContext,
  options: PrepareActionOptions,
): Promise<PreparedAction<P>> {
  const definition = getAction(proposal.actionId) as
    | ActionDefinition<P>
    | null;

  if (!definition) {
    return buildRejectedShell<P>(proposal, {
      reason: "invalid_params",
      message: `Unknown action "${proposal.actionId}".`,
    });
  }

  // 1. Permission gate. UX-only: the real boundary is RLS + audit
  //    lint inside the executor's mutation pipe.
  if (!userMeetsTier(options.userTier, definition.requiredTier)) {
    return buildRejectedShell<P>(proposal, {
      reason: "permission_denied",
      message: `This action requires ${definition.requiredTier} permissions.`,
      definition,
    });
  }

  // 2. Schema validation. We use safeParse so a bad LLM proposal
  //    becomes a rejection rather than a thrown ZodError.
  const parsed = definition.paramsSchema.safeParse(proposal.params);
  if (!parsed.success) {
    return buildRejectedShell<P>(proposal, {
      reason: "invalid_params",
      message: `Invalid parameters: ${parsed.error.issues
        .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
        .join("; ")}`,
      definition,
    });
  }

  // 3. Preview. buildPreview may fetch data (e.g. resolve a person
  //    by id). If it throws — likely an entity-not-found from a
  //    Supabase query — we treat it as a precondition failure and
  //    surface the message so the user understands.
  let preview: { summary: string; detail?: PreparedAction["preview"]["detail"] };
  try {
    preview = await definition.buildPreview(parsed.data, ctx);
  } catch (error) {
    return buildRejectedShell<P>(proposal, {
      reason: "precondition_failed",
      message:
        error instanceof Error
          ? error.message
          : "Could not build a preview for this action.",
      definition,
    });
  }

  return {
    proposal: { ...proposal, params: parsed.data },
    definition,
    preview,
    requiresConfirm:
      options.forceConfirm === true || definition.defaultRequiresConfirm,
    rejection: null,
  };
}

/**
 * Helper: produce a PreparedAction shell for the rejection paths.
 * When we don't have a definition, we synthesize a stub so the
 * caller can still read .preview.summary for display.
 */
function buildRejectedShell<P>(
  proposal: ActionProposal<P>,
  args: ActionRejection & { definition?: ActionDefinition<P> },
): PreparedAction<P> {
  const { definition, ...rejection } = args;
  return {
    proposal,
    // The type contract says definition is non-optional; for
    // unknown-action rejections we cast a minimal stub. UI consumers
    // are expected to render `rejection.message` and ignore the
    // definition when rejection !== null.
    definition:
      definition ??
      ({
        id: proposal.actionId,
        label: proposal.actionId,
        description: "",
        requiredTier: "platform_admin",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        paramsSchema: undefined as any,
        defaultRequiresConfirm: true,
        buildPreview: async () => ({ summary: "" }),
        execute: async () => ({
          ok: false,
          summary: "Action not registered.",
          error: "Action not registered.",
        }),
      } satisfies ActionDefinition<P>),
    preview: { summary: rejection.message },
    requiresConfirm: true,
    rejection,
  };
}
